import type { Punchlist, PunchlistSummary, PunchlistFilters, FieldType } from '../types';

export function getItemCountsForPunchlist(punchlist: Punchlist): { total: number; completed: number } {
  let total = 0;
  let completed = 0;
  for (const section of punchlist.sections) {
    for (const item of section.items) {
      total++;
      if (item.is_completed) completed++;
    }
  }
  return { total, completed };
}

export function computeCompletionPercent(punchlist: Punchlist): number {
  const { total, completed } = getItemCountsForPunchlist(punchlist);
  if (total === 0) return 0;
  return Math.round((completed / total) * 100);
}

export function derivePunchlistStatus(
  punchlist: Punchlist
): 'completed' | 'in_progress' | 'empty' {
  const { total, completed } = getItemCountsForPunchlist(punchlist);
  if (total === 0) return 'empty';
  if (completed === total) return 'completed';
  return 'in_progress';
}

export function computeDashboardData(punchlists: Punchlist[]): PunchlistSummary {
  const itemsByFieldType: Record<FieldType, number> = {
    Condition: 0,
    'Yes/No': 0,
    'Free Text': 0,
    Photo: 0,
    Signature: 0,
  };

  let totalItems = 0;
  let completedItems = 0;
  let completedCount = 0;
  let inProgressCount = 0;
  let templateCount = 0;
  let completionPercentSum = 0;

  for (const pl of punchlists) {
    if (pl.is_template) {
      templateCount++;
    }

    const status = derivePunchlistStatus(pl);
    if (status === 'completed') completedCount++;
    else if (status === 'in_progress') inProgressCount++;

    completionPercentSum += computeCompletionPercent(pl);

    for (const section of pl.sections) {
      for (const item of section.items) {
        totalItems++;
        if (item.is_completed) completedItems++;
        itemsByFieldType[item.field_type] = (itemsByFieldType[item.field_type] || 0) + 1;
      }
    }
  }

  return {
    total: punchlists.length,
    completed: completedCount,
    inProgress: inProgressCount,
    templates: templateCount,
    totalItems,
    completedItems,
    itemsByFieldType,
    averageCompletionPercent:
      punchlists.length > 0 ? Math.round(completionPercentSum / punchlists.length) : 0,
  };
}

export function filterPunchlists(
  punchlists: Punchlist[],
  filters: PunchlistFilters
): Punchlist[] {
  let result = punchlists;

  if (filters.search.trim()) {
    const term = filters.search.toLowerCase();
    result = result.filter(
      (pl) =>
        pl.title.toLowerCase().includes(term) ||
        pl.description.toLowerCase().includes(term)
    );
  }

  if (filters.status !== 'all') {
    result = result.filter((pl) => {
      const status = derivePunchlistStatus(pl);
      return status === filters.status;
    });
  }

  if (filters.type !== 'all') {
    result = result.filter((pl) =>
      filters.type === 'template' ? pl.is_template : !pl.is_template
    );
  }

  const sortMultiplier = filters.sortOrder === 'asc' ? 1 : -1;

  result = [...result].sort((a, b) => {
    switch (filters.sortBy) {
      case 'title':
        return a.title.localeCompare(b.title) * sortMultiplier;
      case 'updated_at':
        return (
          (new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime()) * sortMultiplier
        );
      case 'created_at':
        return (
          (new Date(a.created_at).getTime() - new Date(b.created_at).getTime()) * sortMultiplier
        );
      case 'completion':
        return (computeCompletionPercent(a) - computeCompletionPercent(b)) * sortMultiplier;
      case 'items': {
        const aCount = getItemCountsForPunchlist(a).total;
        const bCount = getItemCountsForPunchlist(b).total;
        return (aCount - bCount) * sortMultiplier;
      }
      default:
        return 0;
    }
  });

  return result;
}

const FIELD_TYPE_BADGE_COLORS: Record<FieldType, string> = {
  Condition: 'bg-blue-100 text-blue-800',
  'Yes/No': 'bg-green-100 text-green-800',
  'Free Text': 'bg-gray-100 text-gray-800',
  Photo: 'bg-purple-100 text-purple-800',
  Signature: 'bg-orange-100 text-orange-800',
};

export function formatFieldTypeBadgeColor(fieldType: FieldType): string {
  return FIELD_TYPE_BADGE_COLORS[fieldType] ?? 'bg-gray-100 text-gray-800';
}
